/**
 * Unified Proxy accessor for `b_repeat.instances`. Each `instances[N]` is a row
 * aggregate carrying `currentIndex`, `currentPrimaryKey`, and the full resolved
 * state (with method-dispatch wrappers) of every direct child block at row N.
 *
 * Lazy: `length` is O(1); `instances[N]` resolves only that row; iteration
 * methods materialize on demand. Within one accessor lifetime each row N is
 * built once and reused. Nested Repeatables get their own accessor attached by
 * `attachInstances` recursion — this file does not recurse.
 *
 * Method dispatch: each child instance's methods are wrapped to fire through
 * the injected `callMethod` (no-op in readonly mode).
 */

import type { RepeatableBlockStateType } from '@unifyapps/defs/blocks/Repeatable/types';
import { getPrimaryKeyValue } from '../getPrimaryKeyValue';
import { CM_SCOPE_SCHEMA, type ScopeSchemaNode } from '../scopeSchema';
import { isRuntimeProbeProp } from './runtimeProbeProps';

export interface ResolvedInstance extends Record<string, unknown> {
  id: string;
}

export interface InstancesAccessorConfig {
  parentData: readonly unknown[] | undefined;
  parentPrimaryKey: RepeatableBlockStateType['content']['primaryKey'];
  /** When present, `buildRow` skips per-row `getPrimaryKeyValue` evaluator calls. */
  itemPrimaryKeys?: readonly string[];
  /** When present, `byKey` / `indexOfKey` are O(1) instead of O(N) scan. */
  itemKeyToIndex?: ReadonlyMap<string, number>;
  /** Direct children only — nested Repeatables are attached by recursion. */
  childBlockIds: string[];
  resolveChildInstance: (
    childBlockId: string,
    primaryKeyValue: string,
  ) => ResolvedInstance | undefined;
  getMethodNamesForBlock?: (childBlockId: string) => readonly string[];
  /** Fire-and-forget; the closure owns try/catch + logging. */
  callMethod: (params: {
    blockId: string;
    targetBlockId: string;
    methodName: string;
    methodPayload: unknown;
  }) => void;
}

export type InstancesAccessor = RowAggregate[];

type RowAggregate = Record<string, unknown> & {
  currentIndex: number;
  currentPrimaryKey: string;
};

// Array methods that mutate in place. Blocked because the accessor caches the
// materialized rows; mutators would corrupt subsequent reads on the same proxy
// without any visible signal to the caller.
const MUTATING_ARRAY_METHODS = new Set([
  'push',
  'pop',
  'shift',
  'unshift',
  'splice',
  'sort',
  'reverse',
  'fill',
  'copyWithin',
]);

// Dedupe console.warn across all accessor instances. Without it, a binding that
// uses an unknown prop inside a render loop (very common in this codebase) would
// flood the console every frame. Key encodes (context, prop) so different proxies
// share dedupe state but distinct typos still surface.
const warnedKeys = new Set<string>();
const warnOnce = (context: string, prop: string): void => {
  const key = `${context}:${prop}`;
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  // eslint-disable-next-line no-console
  console.warn('[Repeatable.instances]', `unknown property "${prop}" on ${context}`);
};

const wrapResolvedWithMethods = (
  resolved: ResolvedInstance,
  childBlockId: string,
  methodNames: readonly string[],
  callMethod: InstancesAccessorConfig['callMethod'],
): ResolvedInstance => {
  const targetBlockId = resolved.id;
  const withMethods: ResolvedInstance = { ...resolved };
  const dispatch = (methodName: string, payload: unknown): void => {
    callMethod({
      blockId: childBlockId,
      targetBlockId,
      methodName,
      methodPayload: payload,
    });
  };
  for (const methodName of methodNames) {
    withMethods[methodName] = (payload: unknown) => dispatch(methodName, payload);
  }
  return withMethods;
};

const wrapRowInGuard = (
  row: RowAggregate,
  allowedKeys: ReadonlySet<string>,
  schema: ScopeSchemaNode,
): RowAggregate => {
  return new Proxy(row, {
    get(target, prop) {
      // CM editor reads its shape via this symbol; matrix runtime never does, so
      // this branch is inert in production. Must precede the symbol fall-through.
      if (prop === CM_SCOPE_SCHEMA) return schema;
      if (typeof prop === 'symbol') return Reflect.get(target, prop);
      if (prop === 'toJSON') return () => ({ ...target });
      if (allowedKeys.has(prop as string)) return Reflect.get(target, prop);
      // React/devtools/JSON.stringify probe well-known names on every Proxy;
      // fall through silently — only user-typed identifiers should warn.
      if (isRuntimeProbeProp(prop as string)) return undefined;
      warnOnce('row', String(prop));
      return undefined;
    },
  }) as RowAggregate;
};

export function createInstancesAccessor(config: InstancesAccessorConfig): InstancesAccessor {
  const {
    parentData,
    parentPrimaryKey,
    itemPrimaryKeys,
    itemKeyToIndex,
    childBlockIds,
    resolveChildInstance,
    getMethodNamesForBlock,
    callMethod,
  } = config;

  const dataLength = parentData?.length ?? 0;
  const cache = new Map<number, RowAggregate>();
  const rowAllowedKeys = new Set<string>(['currentIndex', 'currentPrimaryKey', ...childBlockIds]);

  // Runtime-derived shape advertised to the CM editor's DTS synthesizer (via
  // CM_SCOPE_SCHEMA). Child instances have dynamic field sets, so they're typed
  // as open records — enough for `inst.<childBlockId>` to resolve without
  // claiming a false closed shape. Built once; pure data, no runtime cost.
  const rowSchema: ScopeSchemaNode = {
    kind: 'object',
    properties: {
      currentIndex: { kind: 'primitive', type: 'number' },
      currentPrimaryKey: { kind: 'primitive', type: 'string' },
      ...Object.fromEntries(
        childBlockIds.map((childBlockId) => [childBlockId, { kind: 'record' } as ScopeSchemaNode]),
      ),
    },
  };
  const arraySchema: ScopeSchemaNode = { kind: 'array', element: rowSchema };

  const buildRow = (index: number): RowAggregate | undefined => {
    if (cache.has(index)) return cache.get(index);
    const dataItem = parentData?.[index];
    if (dataItem === undefined) return undefined;

    const primaryKeyValue =
      itemPrimaryKeys?.[index] ??
      String(getPrimaryKeyValue({ dataItem, index, primaryKey: parentPrimaryKey }));

    const row: RowAggregate = {
      currentIndex: index,
      currentPrimaryKey: primaryKeyValue,
    };

    for (const childBlockId of childBlockIds) {
      const resolved = resolveChildInstance(childBlockId, primaryKeyValue);
      if (!resolved) {
        row[childBlockId] = undefined;
        continue;
      }
      const methodNames = getMethodNamesForBlock?.(childBlockId) ?? [];
      row[childBlockId] = wrapResolvedWithMethods(resolved, childBlockId, methodNames, callMethod);
    }

    const guarded = wrapRowInGuard(row, rowAllowedKeys, rowSchema);
    cache.set(index, guarded);
    return guarded;
  };

  // Built once per accessor lifetime. The accessor itself is rebuilt on
  // dataLength/data change by attachInstances, so a single field cache suffices
  // — chained iteration calls (`.filter(...).map(...)`) reuse the same array.
  let materializedCache: RowAggregate[] | undefined;
  const materializeAll = (): RowAggregate[] => {
    if (materializedCache) return materializedCache;
    const out: RowAggregate[] = [];
    for (let index = 0; index < dataLength; index += 1) {
      const row = buildRow(index);
      if (row) out.push(row);
    }
    materializedCache = out;
    return out;
  };

  const findByKey = (primaryKey: unknown): RowAggregate | undefined => {
    const target = String(primaryKey);
    if (itemKeyToIndex) {
      const index = itemKeyToIndex.get(target);
      return index !== undefined ? buildRow(index) : undefined;
    }
    // fallback: full scan for callers without a published index map
    for (let index = 0; index < dataLength; index += 1) {
      const row = buildRow(index);
      if (row && row.currentPrimaryKey === target) return row;
    }
    return undefined;
  };

  const indexOfKeyImpl = (primaryKey: unknown): number => {
    const target = String(primaryKey);
    if (itemKeyToIndex) {
      return itemKeyToIndex.get(target) ?? -1;
    }
    for (let index = 0; index < dataLength; index += 1) {
      const row = buildRow(index);
      if (row && row.currentPrimaryKey === target) return index;
    }
    return -1;
  };

  const keysImpl = (): string[] => {
    if (itemPrimaryKeys) return itemPrimaryKeys.slice();
    const out: string[] = [];
    for (let index = 0; index < dataLength; index += 1) {
      const row = buildRow(index);
      if (row) out.push(row.currentPrimaryKey);
    }
    return out;
  };

  return new Proxy([] as RowAggregate[], {
    get(_target, prop) {
      // CM editor reads the array's element shape via this symbol; matrix
      // runtime never does. Inert branch in production; must win before any
      // string/symbol dispatch below.
      if (prop === CM_SCOPE_SCHEMA) return arraySchema;
      if (prop === 'length') return dataLength;
      if (prop === Symbol.iterator) {
        return function* iterate() {
          for (let index = 0; index < dataLength; index += 1) yield buildRow(index);
        };
      }
      if (typeof prop === 'string' && /^\d+$/.test(prop)) {
        const index = Number(prop);
        if (index >= 0 && index < dataLength) return buildRow(index);
        return undefined;
      }

      // Custom semantics — must win dispatch before the Array.prototype
      // fall-through. `keys` in particular shadows `Array.prototype.keys`
      // (we return primary-key strings; the spec returns an index iterator).
      if (prop === 'byKey') return findByKey;
      if (prop === 'indexOfKey') return indexOfKeyImpl;
      if (prop === 'keys') return keysImpl;
      if (prop === 'toJSON') return () => materializeAll();

      if (typeof prop === 'symbol') return undefined;

      // Auto-forward any non-mutating Array.prototype function to the
      // materialized snapshot. Future spec additions (`toSorted`, `findLast`,
      // `at`, …) work without touching this file. Mutators are blocked
      // because the materialized cache is shared across calls and would
      // silently corrupt subsequent reads.
      if (!MUTATING_ARRAY_METHODS.has(prop)) {
        const protoVal = (Array.prototype as unknown as Record<string, unknown>)[prop];
        if (typeof protoVal === 'function') {
          const arr = materializeAll();
          return (protoVal as (...args: unknown[]) => unknown).bind(arr);
        }
      }

      if (isRuntimeProbeProp(prop)) return undefined;
      warnOnce('instances', String(prop));
      return undefined;
    },
  }) as InstancesAccessor;
}
